import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Clock, Users, Trash2, Search, ArrowUpDown, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const maskValue = (value, show) => show ? value : '******';

const SortHeader = ({ label, field, sortState, onSort, className = '' }) => (
  <TableHead className={`cursor-pointer hover:bg-muted/50 ${className}`} onClick={() => onSort(field)}>
    <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortState.field === field ? 'text-primary' : 'text-muted-foreground/50'}`} />
    </div>
  </TableHead>
);

export const LaborCostTab = ({ teamMembers, teamSalaries, laborCostsByProject, formatCurrency, onRefresh }) => {
  const [isSalaryDialogOpen, setIsSalaryDialogOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ user_id: '', monthly_salary: '', hourly_rate: '', daily_rate: '' });
  const [laborSearch, setLaborSearch] = useState('');
  const [laborSort, setLaborSort] = useState({ field: 'name', order: 'asc' });
  const [showSalaryData, setShowSalaryData] = useState(false);

  const toggleLaborSort = (field) => {
    setLaborSort(prev => ({ field, order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc' }));
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/team-salaries`, {
        ...salaryForm,
        monthly_salary: parseFloat(salaryForm.monthly_salary),
        hourly_rate: parseFloat(salaryForm.hourly_rate),
        daily_rate: parseFloat(salaryForm.daily_rate)
      }, { withCredentials: true });
      toast.success('Salary information saved');
      setIsSalaryDialogOpen(false);
      setSalaryForm({ user_id: '', monthly_salary: '', hourly_rate: '', daily_rate: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save salary information');
    }
  };

  const handleDeleteSalary = async (salaryId) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      await axios.delete(`${API_URL}/team-salaries/${salaryId}`, { withCredentials: true });
      toast.success('Salary record deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete salary record');
    }
  };

  const filteredLaborCosts = useMemo(() => {
    let result = laborCostsByProject;
    if (laborSearch) {
      const term = laborSearch.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.project_code && p.project_code.toLowerCase().includes(term))
      );
    }
    return [...result].sort((a, b) => {
      let aVal = a[laborSort.field];
      let bVal = b[laborSort.field];
      if (typeof aVal === 'string') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return laborSort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = aVal || 0;
      bVal = bVal || 0;
      return laborSort.order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [laborCostsByProject, laborSearch, laborSort]);

  return (
    <div className="space-y-4" data-testid="labor-cost-tab">
      {/* Team Salary Table */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" /> Team Salary Information
          </h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowSalaryData(!showSalaryData)} data-testid="toggle-salary-visibility">
              {showSalaryData ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
              {showSalaryData ? 'Hide Data' : 'Show Data'}
            </Button>
          <Dialog open={isSalaryDialogOpen} onOpenChange={setIsSalaryDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add/Update Salary</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add/Update Team Member Salary</DialogTitle></DialogHeader>
              <form onSubmit={handleSalarySubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Team Member</label>
                  <Select value={salaryForm.user_id || '__none__'} onValueChange={(v) => setSalaryForm({ ...salaryForm, user_id: v === '__none__' ? '' : v })} required>
                    <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select --</SelectItem>
                      {teamMembers.map(m => (
                        <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Monthly Salary (INR)</label>
                  <Input type="number" value={salaryForm.monthly_salary} onChange={(e) => setSalaryForm({ ...salaryForm, monthly_salary: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Hourly Rate (INR)</label>
                    <Input type="number" step="0.01" value={salaryForm.hourly_rate} onChange={(e) => setSalaryForm({ ...salaryForm, hourly_rate: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Daily Rate (INR)</label>
                    <Input type="number" step="0.01" value={salaryForm.daily_rate} onChange={(e) => setSalaryForm({ ...salaryForm, daily_rate: e.target.value })} required />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsSalaryDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Save</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {teamSalaries.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No salary information added yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team Member</TableHead>
                <TableHead className="text-right">Monthly Salary</TableHead>
                <TableHead className="text-right">Hourly Rate</TableHead>
                <TableHead className="text-right">Daily Rate</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamSalaries.map(s => (
                <TableRow key={s.salary_id}>
                  <TableCell className="font-medium">{s.user_name}</TableCell>
                  <TableCell className="text-right">{maskValue(formatCurrency(s.monthly_salary), showSalaryData)}</TableCell>
                  <TableCell className="text-right">{maskValue(`${formatCurrency(s.hourly_rate)}/hr`, showSalaryData)}</TableCell>
                  <TableCell className="text-right">{maskValue(`${formatCurrency(s.daily_rate)}/day`, showSalaryData)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteSalary(s.salary_id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Labor Cost per Project - Column layout with sorting */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
              <Clock className="h-5 w-5" /> Labor Cost per Project
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Calculated as: Hourly Rate x Billable Hours per team member
            </p>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={laborSearch}
              onChange={(e) => setLaborSearch(e.target.value)}
              placeholder="Filter by code or name..."
              className="pl-9"
              data-testid="labor-search"
            />
          </div>
        </div>
        {filteredLaborCosts.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No projects to analyze</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader label="Code" field="project_code" sortState={laborSort} onSort={toggleLaborSort} />
                <SortHeader label="Project" field="name" sortState={laborSort} onSort={toggleLaborSort} />
                <SortHeader label="Team Members" field="userBreakdown" sortState={laborSort} onSort={toggleLaborSort} />
                <SortHeader label="Total Hours" field="totalBillableHours" sortState={laborSort} onSort={toggleLaborSort} className="text-right" />
                <SortHeader label="Total Labor Cost" field="totalLaborCost" sortState={laborSort} onSort={toggleLaborSort} className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLaborCosts.map(p => (
                <TableRow key={p.project_id}>
                  <TableCell className="text-sm text-primary/70">{p.project_code || '-'}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>
                    {p.userBreakdown.length > 0 ? (
                      <div className="space-y-1">
                        {p.userBreakdown.map((u, idx) => (
                          <div key={`${u.name}-${u.userId || idx}`} className="text-sm">
                            <span>{u.name}</span>
                            <span className="text-muted-foreground ml-2">({(u.billableHours || 0).toFixed(1)}h @ {maskValue(formatCurrency(u.hourlyRate || 0), showSalaryData)}/hr = {maskValue(formatCurrency(u.cost || 0), showSalaryData)})</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">No time logged</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{(p.totalBillableHours || 0).toFixed(1)}h</TableCell>
                  <TableCell className="text-right font-semibold text-orange-600">{maskValue(formatCurrency(p.totalLaborCost || 0), showSalaryData)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};
